import { useMemo, useState } from 'react';

import { Link, NotFound, Pagination, Typography, Wrapper } from '@/components';
import { useAuthUser, useAuthToken } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { useCampaignPlans } from '@/hooks/client';
import { getPlannerUrlWithJwt } from '@/utils/links';

import styles from './CampaignPlans.module.css';
import { CampaignPlan } from './components/CampaignPlan';

export function CampaignPlans() {
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();
  const user = useAuthUser();
  const jwtToken = useAuthToken();

  const plannerUrl = getPlannerUrlWithJwt(jwtToken);

  const { data, isLoading } = useCampaignPlans({ currentPage });

  const memoData = useMemo(() => data || {}, [data]);

  if (!user?.is_client) return null;

  function prevPage() {
    return setCurrentPage((prevState) => Math.max(1, prevState - 1));
  }

  function nextPage() {
    return setCurrentPage((prevState) => prevState + 1);
  }

  // Calculate max pages based on hasMore - if hasMore is true, there are more pages
  // For now, we'll show pagination if hasMore is true or if we're not on page 1
  const maxPages = memoData.hasMore ? currentPage + 1 : currentPage;

  return (
    <Wrapper
      isLoading={isLoading}
      heading={
        <div className={styles.campaignPlansHeading}>
          <div className={styles.campaignPlansHeadingTeam}>
            <Typography sizes={['h2']}>
              <h2>{t('plans.heading')}</h2>
            </Typography>
          </div>

          <div className={styles.campaignPlansHeadingColumn}>
            <Link href={plannerUrl} theme="cta" target="_blank">
              {t('plans.cta')}
            </Link>
          </div>
        </div>
      }
    >
      {memoData.drafts?.length ? (
        <>
          {(maxPages > 1 || currentPage > 1) && (
            <div className={styles.campaignPlansPagination}>
              <Pagination
                currentPage={currentPage - 1}
                handleNextPage={nextPage}
                handlePrevPage={prevPage}
                maxPages={maxPages}
                hasMore={memoData.hasMore}
              />
            </div>
          )}

          <div className={styles.campaignPlansList}>
            {memoData.drafts?.map((draft: Api.Planner.v1PublicCampaignDraft) => (
              <CampaignPlan key={`campaign-plan-${draft.draft_id}`} draft={draft} />
            ))}
          </div>

          {(maxPages > 1 || currentPage > 1) && (
            <div className={styles.campaignPlansPagination}>
              <Pagination
                currentPage={currentPage - 1}
                handleNextPage={nextPage}
                handlePrevPage={prevPage}
                maxPages={maxPages}
                hasMore={memoData.hasMore}
              />
            </div>
          )}
        </>
      ) : (
        <div className={styles.campaignPlansNotFound}>
          <NotFound text={t('plans.notFound')} />
        </div>
      )}
    </Wrapper>
  );
}
